import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ShieldCheck, CheckCircle, Warning, ClipboardText } from "@phosphor-icons/react";

const ORG_TYPE_LABELS = {
  youth_club: "Youth Club",
  academy: "Academy",
  league: "League",
  school: "High School",
  tournament: "Tournament",
};

export default function PublicBadge() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Use plain axios (no credentials) — this is a public endpoint
    axios.get(`${API}/public/badge/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-800 grid place-items-center mx-auto mb-4">
            <ShieldCheck size={30} weight="duotone" className="text-slate-500" />
          </div>
          <h1 className="text-2xl font-display font-black tracking-tight mb-2">Public badge not available</h1>
          <p className="text-slate-400 max-w-md">This organization either doesn't exist or hasn't enabled public sharing.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-slate-950 grid place-items-center text-slate-400">Loading…</div>;
  }

  const score = data.audit_readiness_score;
  const scoreColor = score >= 85 ? "text-emerald-400" : score >= 60 ? "text-amber-400" : "text-rose-400";
  const scoreBorder = score >= 85 ? "border-emerald-500/30" : score >= 60 ? "border-amber-500/30" : "border-rose-500/30";
  const scoreBg = score >= 85 ? "bg-emerald-500/5" : score >= 60 ? "bg-amber-500/5" : "bg-rose-500/5";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 py-10 px-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />

      <div className="max-w-3xl mx-auto relative">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-xl bg-orange-500 grid place-items-center">
            <ShieldCheck size={20} weight="bold" color="#fff7ed" />
          </div>
          <div>
            <div className="font-display font-black text-slate-50 leading-none text-lg">AuditReady <span className="text-orange-400">Sports</span></div>
            <div className="font-label text-orange-400 mt-1">PUBLIC AUDIT-READINESS BADGE</div>
          </div>
        </div>

        {/* Main card */}
        <div className="rounded-3xl border border-white/10 bg-slate-900 p-8 md:p-10 shadow-2xl" data-testid="public-badge-card">
          <div className="text-center mb-8">
            {data.logo_url && (
              <img src={data.logo_url} alt="" className="w-20 h-20 rounded-2xl object-cover mx-auto mb-4" />
            )}
            <div className="font-label text-orange-400 mb-2">{ORG_TYPE_LABELS[data.org_type] || "Sports Organization"}</div>
            <h1 className="text-3xl md:text-4xl font-display font-black tracking-tight" data-testid="public-org-name">{data.org_name}</h1>
          </div>

          {/* Score ring */}
          {data.show_score !== false && (
            <div className={`rounded-2xl border ${scoreBorder} ${scoreBg} p-8 text-center mb-6`}>
              <div className="font-label text-slate-400 mb-3">AUDIT READINESS SCORE</div>
              <div className={`text-7xl md:text-8xl font-display font-black tracking-tight ${scoreColor}`} data-testid="audit-readiness-score">
                {score}%
              </div>
              <div className="text-slate-400 text-sm mt-3 max-w-sm mx-auto">
                Based on the ratio of current credentials to total credentials tracked in AuditReady Sports.
              </div>
            </div>
          )}

          {/* Description */}
          {data.description && (
            <div className="rounded-xl border border-white/10 bg-slate-950 p-4 mb-6 text-center text-sm text-slate-300" data-testid="public-description">
              {data.description}
            </div>
          )}

          {/* Stats */}
          {data.show_counts !== false && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="rounded-xl border border-white/10 bg-slate-950 p-4 text-center">
                <div className="text-2xl font-display font-black text-slate-100">{data.active_credentials}</div>
                <div className="text-xs text-slate-400 mt-1">Active credentials</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-950 p-4 text-center">
                <div className="text-2xl font-display font-black text-amber-300">{data.expiring_or_missing}</div>
                <div className="text-xs text-slate-400 mt-1">Expiring or missing</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-950 p-4 text-center">
                <div className="text-2xl font-display font-black text-slate-100">{data.total_credentials}</div>
                <div className="text-xs text-slate-400 mt-1">Total tracked</div>
              </div>
            </div>
          )}

          <div className="text-center text-xs text-slate-500 mb-6">
            Last updated {new Date(data.last_updated).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
          </div>

          <div className="pt-6 border-t border-white/10 text-center">
            <div className="font-label text-slate-500 text-[10px]">POWERED BY</div>
            <div className="font-display font-black text-slate-100 mt-1">{data.powered_by}</div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/70 p-5 flex gap-3" data-testid="public-badge-disclaimer">
          <ClipboardText size={16} weight="bold" className="text-slate-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-label text-slate-500 mb-2">IMPORTANT</div>
            <p className="text-xs leading-relaxed text-slate-500">
              The Audit Readiness Score reflects <span className="text-slate-400 font-semibold">only records tracked in AuditReady Sports</span> and is NOT a
              certification of legal compliance by SafeSport, U.S. Soccer (USSF), FIFA, NFHS, BOC, any school district, or any governing body.
              AuditReady Sports is an independent record-keeping tool and does not verify credentials with issuing organizations.
              No personally identifiable information, employee names, credential numbers, documents, medical data, or background-check details
              are shown on this page. To confirm actual compliance status, contact the organization directly.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-xs text-slate-500 hover:text-orange-300">Are you a sports organization? Try AuditReady Sports →</a>
        </div>
      </div>
    </div>
  );
}
